/**
 * Normalized, user-facing playback / network messages (frontend-only, no CORS bypass).
 */

const isDev = (): boolean => process.env.NODE_ENV !== 'production';

/** Detailed logs in development only. */
export function devPlayerLog(...args: unknown[]): void {
  if (isDev()) {
    // eslint-disable-next-line no-console
    console.log('[VoraKit StreamPlayer]', ...args);
  }
}

/** Generic blocked / unsupported stream (legacy copy + video fallback). */
export const PLAYBACK_GENERIC_FAILURE =
  'This stream is blocked by the provider (CORS / authorization), or your browser cannot play it. Try another channel, or use “Open in new tab”.';

export const PLAYBACK_CORS_OR_BLOCKED =
  'Provider blocked browser access (CORS). Try another source or use a native IPTV app.';

/**
 * Classify `fetch` failures (no Response) and a few thrown strings from adapters.
 */
export function messageFromFetchFailure(e: unknown): string {
  const raw = e instanceof Error ? e.message : String(e);
  const m = raw.toLowerCase();

  if (/invalid username|invalid password|authentication failed|auth.*failed|user_info.*auth.*0/i.test(raw)) {
    return 'Invalid username or password. Please verify your IPTV credentials.';
  }
  if (/failed to fetch|networkerror|network request failed|load failed/i.test(m)) {
    return PLAYBACK_CORS_OR_BLOCKED;
  }
  if (/enotfound|getaddrinfo|name not resolved|dns|could not resolve|nxdomain/i.test(m)) {
    return 'Invalid server URL. Make sure you include the correct host and port.';
  }
  if (/aborted|timeout|timed out|etimedout/i.test(m)) {
    return 'Request timed out. Check the URL, port, and your connection.';
  }
  if (/ssl|certificate|cert_/i.test(m)) {
    return 'Secure connection failed. Check that the URL uses the correct protocol (http vs https).';
  }
  return `Could not reach the server. ${raw}`;
}

/** `<video>` `MediaError.code` → short UI string. */
export function messageFromMediaErrorCode(code: number | undefined | null): string {
  if (code == null || code === 0) {
    return PLAYBACK_GENERIC_FAILURE;
  }
  switch (code) {
    case MediaError.MEDIA_ERR_ABORTED:
      return 'Playback was interrupted.';
    case MediaError.MEDIA_ERR_NETWORK:
      return 'Stream not reachable.';
    case MediaError.MEDIA_ERR_DECODE:
      return 'Stream format not supported.';
    case MediaError.MEDIA_ERR_SRC_NOT_SUPPORTED:
      return 'Invalid stream URL.';
    default:
      return PLAYBACK_GENERIC_FAILURE;
  }
}
